import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { ActivityEvent, ActivityEventType } from '@/shared/types/activityEvent';
import { activityFeedService } from '@/shared/services/activityFeedService';
import type { ActivityFeedOptions } from '@/shared/services/activityFeedService';
import { useAuthStore } from '@/shared/stores/authStore';

/**
 * Options for the useActivityFeed hook
 */
interface UseActivityFeedOptions {
  /** Number of days to look back (default: 30) */
  timeRange?: number;
  
  /** Maximum number of events to return (default: 100) */
  limit?: number;
  
  /** Filter to specific event types */
  eventTypes?: ActivityEventType[];
}

/**
 * Return type for the useActivityFeed hook
 */
interface UseActivityFeedReturn {
  /** Array of activity events */
  events: ActivityEvent[];
  
  /** Loading state indicator */
  loading: boolean;
  
  /** Error object if fetch failed */
  error: Error | null;
  
  /** Function to manually refresh the feed */
  refresh: () => Promise<void>;
}

/**
 * Custom React hook for fetching activity feed with real-time updates
 * 
 * This hook:
 * - Fetches personalized activity events from the backend for a specific domain
 * - Subscribes to real-time updates using Firestore listeners
 * - Handles loading, error, and empty states
 * - Cleans up subscriptions on unmount
 * - Provides a manual refresh function
 * 
 * Domain-Agnostic Design:
 * - Requires domain parameter to scope events to a specific business domain
 * - Works with any domain (workflow, hiring, billing, goals, etc.)
 * - Uses shared activityFeedService for cross-domain compatibility
 * 
 * @param domain - The business domain to fetch events for (e.g., 'workflow', 'hiring')
 * @param options - Configuration options for the feed
 * @returns Activity feed state and control functions
 * 
 * @example
 * ```tsx
 * // Workflow domain
 * const { events, loading, error, refresh } = useActivityFeed('workflow', {
 *   timeRange: 30,
 *   limit: 100
 * });
 * 
 * // Hiring domain
 * const { events, loading, error } = useActivityFeed('hiring', {
 *   timeRange: 90,
 *   eventTypes: ['hiring_spot.created', 'hiring_spot.filled']
 * });
 * 
 * if (loading) return <LoadingState />;
 * if (error) return <ErrorState error={error} onRetry={refresh} />;
 * if (events.length === 0) return <EmptyState />;
 * 
 * return <EventList events={events} />;
 * ```
 */
export function useActivityFeed(
  domain: string,
  options: UseActivityFeedOptions = {}
): UseActivityFeedReturn {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Get the effective user from auth store
  const effectiveUser = useAuthStore((state: any) => state.effectiveUser);
  const userId = effectiveUser?.uid;
  
  // Stabilize eventTypes array to prevent unnecessary subscription recreations
  // Only create new reference when actual values change
  const stableEventTypes = useMemo(() => {
    if (!options.eventTypes || options.eventTypes.length === 0) {
      return undefined;
    }
    // Sort to ensure consistent comparison
    return [...options.eventTypes].sort();
  }, [options.eventTypes ? JSON.stringify([...options.eventTypes].sort()) : undefined]);
  
  // Debounce timer for batching rapid updates
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingEventsRef = useRef<ActivityEvent[] | null>(null);

  /**
   * Manual refresh function to re-fetch the feed
   */
  const refresh = useCallback(async () => {
    if (!userId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const feedOptions: ActivityFeedOptions = {
        timeRange: options.timeRange || 30,
        limit: options.limit || 100,
        eventTypes: stableEventTypes,
      };

      const fetchedEvents = await activityFeedService.getPersonalizedFeed(
        domain,
        userId,
        feedOptions
      );

      setEvents(fetchedEvents);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch activity feed';
      setError(new Error(errorMessage));
    } finally {
      setLoading(false);
    }
  }, [domain, userId, options.timeRange, options.limit, stableEventTypes]);

  /**
   * Debounced event update handler
   * Batches rapid updates within 300ms window to reduce re-renders
   */
  const debouncedSetEvents = useCallback((updatedEvents: ActivityEvent[], isInitialLoad: boolean = false) => {
    // For initial load, apply immediately without debouncing
    if (isInitialLoad) {
      setEvents(updatedEvents);
      setLoading(false);
      return;
    }

    // Clear any pending timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Store the latest events
    pendingEventsRef.current = updatedEvents;

    // Set a timer to apply the update after 300ms
    debounceTimerRef.current = setTimeout(() => {
      if (pendingEventsRef.current) {
        setEvents(pendingEventsRef.current);
        setLoading(false);
        pendingEventsRef.current = null;
      }
    }, 300);
  }, [domain]);

  /**
   * Set up real-time subscription to activity feed
   */
  useEffect(() => {
    // If user is not logged in, clear the feed and stop loading
    if (!userId) {
      setEvents([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const feedOptions: ActivityFeedOptions = {
      timeRange: options.timeRange || 30,
      limit: options.limit || 100,
      eventTypes: stableEventTypes,
    };

    // Track if this is the first data load
    let isFirstLoad = true;

    // Subscribe to real-time updates with debouncing
    const unsubscribe = activityFeedService.subscribeToPersonalizedFeed(
      domain,
      userId,
      (updatedEvents) => {
        debouncedSetEvents(updatedEvents, isFirstLoad);
        isFirstLoad = false;
      },
      feedOptions,
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    // Cleanup subscription and debounce timer on unmount or when dependencies change
    return () => {
      unsubscribe();
      
      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      
      // Apply any pending updates immediately on cleanup
      if (pendingEventsRef.current) {
        setEvents(pendingEventsRef.current);
        pendingEventsRef.current = null;
      }
    };
  }, [domain, userId, options.timeRange, options.limit, stableEventTypes, debouncedSetEvents]);

  return {
    events,
    loading,
    error,
    refresh,
  };
}
